import React from "react";
import {
  BarChart3,
  ActivitySquare,
  Search,
  FileBarChart,
  CheckCircle,
} from "lucide-react";

export default function GoldenQuadrantOnePager() {
  const data = {
    footer: {
      year: 2024,
      legal: "© 2024 Ackme.ai. All rights reserved.",
      company_name: "Ackme.ai",
    },
    company: {
      logo: null,
      name: "Ackme.ai",
      region: "San Francisco, CA & Global",
      segment: "AI-powered Data Analytics SaaS",
    },
    benefits: [
      {
        title: "Prevent Revenue Leakage Instantly",
        description:
          "Detect anomalies at the source and act in real-time—no more missed bookings, billing errors, or unnoticed pipeline dips.",
      },
      {
        title: "Empower Every Team, Not Just Analysts",
        description:
          "Contextual insights delivered directly to teams in Slack, HubSpot, or CRM—driving unified, confident action at every business level.",
      },
      {
        title: "Slash Manual Analysis, Accelerate Decisions",
        description:
          "Free staff from dashboard overload—Ackme.ai does the heavy lifting, so you focus on what moves the business.",
      },
      {
        title: "Future-Ready, Modular Growth",
        description:
          "Expand analytics, add new sources, or unlock new workflows as Zyphon’s needs evolve—Ackme’s platform adapts without downtime.",
      },
    ],
    cta_section: {
      note: "Or reply to this email with questions ahead of our meeting—we’ll prioritize answers for Zyphon’s team.",
      title: "Ready for Proactive, AI-Driven Business Operations?",
      button: {
        url: "https://ackme.ai/book-demo",
        text: "Book Your Custom Demo",
      },
    },
    roi_metrics: {
      title: "Ackme.ai Impact for Zyphon",
      metrics: [
        {
          desc: "From 48 to 8 hours",
          label: "Reduction in Time-to-Insight",
          value: "83%",
        },
        {
          desc: "Revenue leakage prevented",
          label: "Protected Revenue (Monthly)",
          value: "$1,340,000",
        },
        {
          desc: "Analyst/BI/Operations teams",
          label: "Manual Analysis Hours Saved",
          value: "80%",
        },
        {
          desc: "Proven on live customer data",
          label: "Forecast Accuracy",
          value: "0.91",
        },
        {
          desc: "Connect, onboard, show value",
          label: "Speed to Pilot/POC",
          value: "< 3 days",
        },
      ],
    },
    testimonial: {
      quote:
        "Ackme.ai transformed our decision process—root causes surfaced automatically, so we spent less time digging and more time growing revenue.",
      stats: [
        "83% faster time-to-insight (from 48h to 8h)",
        "$1.34M more monthly revenue protected",
      ],
      company: "Leading Private Equity Portfolio Company",
      author_role: "Head of BI & Analytics",
    },
    hero_section: {
      title:
        "Make Business Intelligence Proactive: Why Ackme.ai is Zyphon’s Top Choice for AI-Driven Operations",
      metrics: [
        {
          label: "Reduction in Time-to-Insight",
          value: "83%",
          subLabel: "From 48h to 8h",
        },
        {
          label: "Analyst Hours Saved",
          value: "80%",
          subLabel: "Per Month",
        },
        {
          label: "Revenue Protected",
          value: "$1.34M+",
          subLabel: "Per Month",
        },
      ],
    },
    features_table: {
      title: "Feature Comparison: Ackme.ai vs Alternatives",
      headers: ["Feature", "Ackme.ai", "Tableau", "PowerBI", "Sisense", "Tellius"],
      rows: [
        {
          feature: "Automated Pattern Detection & Root Cause Analysis",
          ackme: "✓ Industry-best (AutoExplain LLM Driven)",
          tableau: "▲ Manual / Limited AI",
          powerbi: "▲ Manual / Some AI",
          sisense: "▲ Semi-automated",
          tellius: "▲ Semi-AI",
        },
        {
          feature: "Action Delivery to Slack/HubSpot/CRM",
          ackme: "✓ Native, API-driven",
          tableau: "▲ Manual export",
          powerbi: "▲ Manual export",
          sisense: "▲ Manual triggers",
          tellius: "▲ Limited API",
        },
        {
          feature: "Cross-Metric & Text Data Reasoning",
          ackme: "✓ Unstructured & structured combined",
          tableau: "✗ Dashboards only",
          powerbi: "✗ Dashboards only",
          sisense: "▲ Partial",
          tellius: "▲ Text-enabled",
        },
        {
          feature: "Continuous KPI Monitoring",
          ackme: "✓ Always-on",
          tableau: "▲ Alerts only",
          powerbi: "▲ Alerts only",
          sisense: "▲ Alerts",
          tellius: "▲ Scheduled",
        },
        {
          feature: "Deployment Time (POC)",
          ackme: "< 3 days (auto onboarding)",
          tableau: "~1 week+",
          powerbi: "~1 week+",
          sisense: "5-7 days",
          tellius: "5-7 days",
        },
      ],
    },
    golden_quadrant: {
      note: "Ackme.ai uniquely sits in the top right—automating insight-to-action and delivering proactive, contextual recommendations far beyond dashboards.",
      title:
        "Why Ackme.ai Wins for Zyphon: Industry-Leading Contextual AI & Proactive Insights",
      subtitle: "Golden Quadrant: AI Analytics Vendors",
      highlight_quadrant: "Contextual Action (Ackme.ai)",
      quadrants: [
        {
          icon: "activity-square",
          label: "Contextual Action (Ackme.ai)",
          description:
            "Real-time root cause detection, proactive recommendations, closed-loop workflows, and automated insight delivery into Slack, HubSpot, and CRMs.",
        },
        {
          icon: "bar-chart-3",
          label: "Traditional BI (Tableau, PowerBI)",
          description:
            "Intuitive dashboards, strong visualization, but manual analysis and limited root cause reasoning.",
        },
        {
          icon: "search",
          label: "Self-Serve AI (ThoughtSpot, Sisense, Tellius)",
          description:
            "Good self-serve analytics, some NLQ, but patchy AI and limited in-context action automation.",
        },
        {
          icon: "file-bar-chart",
          label: "Legacy Tools",
          description:
            "Static reporting with no predictive/AI or workflow integration.",
        },
      ],
    },
    expansion_callout: {
      title: "Future-Proof: Scale as Zyphon Grows",
      bullets: [
        "Modular platform scales from single use case to cross-team, cross-department analytics.",
        "Add new data sources or business units seamlessly—no additional deployments required.",
        "Ackme.ai customers expand from initial POC to company-wide rollouts within months.",
        "Enable self-serve analytics for non-technical users without new setup.",
      ],
    },
    mini_case_studies: [
      {
        customer: "PE Backed Financial Services Client",
        quote:
          "Ackme.ai cut our time-to-insight by over 80% and prevented over $1M in revenue slippage within 60 days.",
        outcome: [
          "Onboarded in <3 days, results visible in 1 week",
          "Analytics streamlined for 4 departments (Ops, BI, Finance, Sales)",
        ],
      },
      {
        customer: "Global SaaS Platform",
        quote:
          "With Ackme.ai, we moved from dashboarding to true AI-guided action—sales and marketing now catch critical issues before they impact the business.",
        outcome: [
          "60% efficiency gain in cross-team reporting",
          "90% AURA insights adoption",
        ],
      },
    ],
  };

  const {
    company,
    hero_section,
    golden_quadrant,
    features_table,
    roi_metrics,
    benefits,
    expansion_callout,
    mini_case_studies,
    testimonial,
    cta_section,
    footer,
  } = data;

  // Map icons to Lucide React components
  const quadrantIcons = {
    "activity-square": <ActivitySquare className="h-6 w-6 text-[#00C271]" />,
    "bar-chart-3": <BarChart3 className="h-6 w-6 text-gray-500" />,
    search: <Search className="h-6 w-6 text-indigo-500" />,
    "file-bar-chart": <FileBarChart className="h-6 w-6 text-gray-400" />,
  };

  return (
    <div className="font-sans bg-white text-gray-900 max-w-5xl mx-auto px-6 py-10">
      {/* HERO SECTION */}
      <section className="flex flex-col md:flex-row justify-between items-center mb-10 gap-6">
        <div className="flex-1">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 text-[#023e8a]">
            {hero_section.title}
          </h1>
          <div className="flex flex-wrap gap-8 my-4">
            {hero_section.metrics.map((m, i) => (
              <div
                key={i}
                className="bg-[#f7fafc] rounded-xl px-6 py-4 text-center shadow-sm min-w-[120px]"
              >
                <div className="text-2xl md:text-3xl font-extrabold text-[#00C271]">
                  {m.value}
                </div>
                <div className="mt-1 text-gray-600 text-xs md:text-sm">
                  {m.label}
                </div>
                {m.subLabel && (
                  <div className="text-xs text-gray-400">{m.subLabel}</div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="font-semibold text-lg text-[#023e8a] mb-1">
            {company.name}
          </div>
          <div className="text-xs text-gray-500">{company.segment}</div>
          <div className="text-xs text-gray-500">{company.region}</div>
        </div>
      </section>

      {/* GOLDEN QUADRANT */}
      <section className="mb-10">
        <div className="flex flex-col md:flex-row gap-8 md:items-center">
          <div className="flex-1">
            <div className="text-xl font-semibold text-[#00C271] mb-1">
              {golden_quadrant.title}
            </div>
            <div className="text-gray-700 text-sm mb-2">
              {golden_quadrant.subtitle}
            </div>
            <div className="gap-4 grid grid-cols-2 md:grid-cols-4 p-4 rounded-lg bg-[#f1f9fc] border border-[#e0e7ef]">
              {golden_quadrant.quadrants.map((q, i) => (
                <div
                  key={i}
                  className={
                    "flex flex-col items-center text-center px-2 py-2 rounded-lg " +
                    (q.label.includes(golden_quadrant.highlight_quadrant)
                      ? "border-2 border-[#00C271] bg-white shadow-lg"
                      : "")
                  }
                >
                  <span>{quadrantIcons[q.icon]}</span>
                  <span className="font-semibold mt-2 text-[#023e8a] text-sm leading-tight">
                    {q.label}
                  </span>
                  <span className="text-[12px] text-gray-600 mt-1">
                    {q.description}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-[#6c757d] italic">
              {golden_quadrant.note}
            </div>
          </div>
        </div>
      </section>

      {/* ...rest of component remains unchanged */}
    </div>
  );
}
